import React, { useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { X } from 'lucide-react';
import { formatDisplayDate, formatDisplayTime } from '../../lib/displayFormat';

/** Values shared by every Business Basic page (see BasicLayout). */
export const useBasic = () => useOutletContext();

/** Dates and times in the person's chosen formats (Settings › Language). */
export const formatDate = (iso) => formatDisplayDate(iso);

export function PageHeading({ title, subtitle, children, crumbs, root = 'Workspace' }) {
  return (
    <div className="page-heading-row">
      <div>
        <div className="breadcrumbs">{root} {(crumbs || [title]).map((c) => <React.Fragment key={c}><span>/</span> {c} </React.Fragment>)}</div>
        <h1>{title}</h1><p>{subtitle}</p>
      </div>
      {children && <div className="page-heading-actions">{children}</div>}
    </div>
  );
}

export function StatCards({ stats }) {
  return (
    <div className="page-stats">
      {stats.map(([label, value, caption], i) => (
        <article className="page-stat" key={label}>
          <span className={`stat-mark mark-${i % 4}`} />
          <div><small>{label}</small><b>{value ?? '…'}</b><span>{caption}</span></div>
        </article>
      ))}
    </div>
  );
}

export function Modal({ title, onClose, children, footer, wide }) {
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`modal ${wide ? 'wide' : ''}`} role="dialog" aria-modal="true" aria-label={title}>
        <div className="modal-head"><h2>{title}</h2><button className="icon-button" aria-label="Close" onClick={onClose}><X size={16} /></button></div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>
  );
}

export function ErrorBanner({ message }) {
  return message ? <div className="form-error" role="alert">{message}</div> : null;
}

export const RolePill = ({ role }) => <span className={`role-pill role-${role}`}>{role}</span>;

export const ComingSoon = () => <span className="soon-tag">Coming soon</span>;

/** Save a blob response (CSV export) as a file in the browser. */
export function saveDownload(res, name) {
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${name}-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/** Local date (YYYY-MM-DD) and time (HH:MM) parts of a UTC timestamp from the API. */
export function localParts(iso) {
  const d = new Date(`${iso}Z`);
  const pad = (n) => String(n).padStart(2, '0');
  return { date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`, time: `${pad(d.getHours())}:${pad(d.getMinutes())}` };
}

export const formatTime = (iso) => formatDisplayTime(iso);
